import { Color, Matrix, Path, Point, PointText } from "paper";
import { defaultGridLineSettings, defaultGridTextSettings, defaultMeasurementLineSettings, } from "../settings/altitude-view-settings";
export var PaperUtils;
(function (PaperUtils) {
    function createText(pos, fillColor, content, fontSize, justification = "center") {
        // to render text of height of control point
        const text = new PointText(new Point(pos.x, -pos.y));
        text.justification = justification;
        text.fontFamily = "Arial";
        text.fillColor = fillColor;
        text.content = content;
        text.fontSize = fontSize;
        text.transform(new Matrix(1, 0, 0, -1, 0, 0));
        return text;
    }
    PaperUtils.createText = createText;
    // just for edit
    function createRegularPolygon(point, sides, radius, fillColor) {
        const triangle = new Path.RegularPolygon(point, sides, radius);
        triangle.fillColor = fillColor;
        triangle.strokeWidth = 0;
        triangle.rotate(180, point);
        return triangle;
    }
    PaperUtils.createRegularPolygon = createRegularPolygon;
    function createPoint(point, radius, fillColor) {
        const circle = new Path.Circle(point, radius);
        circle.fillColor = fillColor;
        circle.strokeWidth = 0;
        circle.rotate(180, point);
        return circle;
    }
    PaperUtils.createPoint = createPoint;
    function createLine(pointA, pointB, strokeColor) {
        const path = new Path();
        path.strokeColor = strokeColor;
        path.add(pointA);
        path.add(pointB);
        return path;
    }
    PaperUtils.createLine = createLine;
    function createGrid(view, pixelStep, lineGroup, textGroup, presentation) {
        // Clear older entities
        lineGroup.removeChildren();
        textGroup.removeChildren();
        const sceneStepAtCurrentZoom = PaperUtils.pixelToProjectDist(pixelStep, view);
        const { sceneStepMeters: step } = presentation.resolveGridScale(sceneStepAtCurrentZoom);
        const visibleRect = view.bounds;
        const horizontalTextOffset = 10;
        // Drawing vertical lines
        // Find the nearest value divisible by step so we get consistent texts
        let xPos = Math.ceil(visibleRect.left / step) * step;
        do {
            const topPoint = new Point(xPos, visibleRect.top);
            const bottomPoint = new Point(xPos, visibleRect.bottom);
            const aLine1 = new Path.Line(topPoint, bottomPoint);
            lineGroup.addChild(aLine1);
            aLine1.strokeColor = defaultGridLineSettings.strokeColor;
            const text = createText(bottomPoint.clone(), defaultGridTextSettings.lightFillColor, `${presentation.formatAxisValue(xPos)}${presentation.getUnitLabel()}`, 1, "center");
            const oldPixelPos = view.projectToView(bottomPoint);
            const newPixePos = new Point(oldPixelPos.x, oldPixelPos.y + horizontalTextOffset);
            const newPos = view.viewToProject(newPixePos);
            text.position.set(newPos);
            textGroup.addChild(text);
            xPos = xPos + step;
        } while (xPos <= visibleRect.right);
        // Drawing horizontal lines
        const verticalTextOffset = 20;
        // Find the nearest value divisible by step so we get consistent texts
        let yPos = Math.ceil(visibleRect.top / step) * step;
        do {
            const leftPoint = new Point(visibleRect.left, yPos);
            const rightPoint = new Point(visibleRect.right, yPos);
            const aLine2 = new Path.Line(leftPoint, rightPoint);
            lineGroup.addChild(aLine2);
            aLine2.strokeColor = defaultGridLineSettings.strokeColor;
            const text = createText(leftPoint, defaultGridTextSettings.lightFillColor, `${presentation.formatAxisValue(yPos)}${presentation.getUnitLabel()}`, 1, "left");
            const oldPixelPos = view.projectToView(leftPoint);
            const newPixePos = new Point(oldPixelPos.x + verticalTextOffset, oldPixelPos.y);
            const newPos = view.viewToProject(newPixePos);
            text.position.set(newPos);
            textGroup.addChild(text);
            yPos = yPos + step;
        } while (yPos <= visibleRect.bottom);
    }
    PaperUtils.createGrid = createGrid;
    function zoom(event, zoomFactor, view) {
        const dir = Math.sign(event.deltaY);
        const scaleFactor = dir === 0 ? 1 : zoomFactor ** (-dir / Math.abs(dir));
        const point = getCoordinateOnView(view, event.pageX, event.pageY);
        view.scale(scaleFactor, point.clone());
    }
    PaperUtils.zoom = zoom;
    function getCoordinateOnView(view, pageX, pageY) {
        const output = new Point(0, 0);
        const scrollLeft = Math.max(window.pageXOffset, document.documentElement.scrollLeft, document.body.scrollLeft);
        const scrollTop = Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop);
        const boundingClientRect = view.element.getBoundingClientRect();
        output.x = pageX - boundingClientRect.left - scrollLeft;
        output.y = pageY - boundingClientRect.top - scrollTop;
        const point = new Point(output.x, output.y);
        const projectedPoint = view.viewToProject(point);
        return projectedPoint;
    }
    PaperUtils.getCoordinateOnView = getCoordinateOnView;
    function fitView(view, position, width, height) {
        const scaleHeight = (view.bounds.height * 70) / 100 / height;
        const scaleWidth = (view.bounds.width * 70) / 100 / width;
        let scale = scaleHeight < scaleWidth ? scaleHeight : scaleWidth;
        scale = Number.isFinite(scale) ? scale : 1;
        const translation = view.center.subtract(position);
        view.translate(translation);
        view.scale(scale, view.center);
    }
    PaperUtils.fitView = fitView;
    function getPointAtDistanceOnLine(startPaperPoint, endPaperPoint, distance) {
        const forwDir = endPaperPoint.subtract(startPaperPoint).normalize();
        const pointAtDistance = new Point(startPaperPoint.x + forwDir.x * distance, startPaperPoint.y + forwDir.y * distance);
        return pointAtDistance;
    }
    PaperUtils.getPointAtDistanceOnLine = getPointAtDistanceOnLine;
    function attenuateEntites(entity, view, sizeInPixel) {
        const newSizeOfEntity = PaperUtils.getCoordinateOnView(view, sizeInPixel, 0).getDistance(PaperUtils.getCoordinateOnView(view, 0, 0));
        const entityCurrentSize = entity.bounds.height;
        const newScale = newSizeOfEntity / entityCurrentSize;
        entity.scale(newScale, entity.position);
    }
    PaperUtils.attenuateEntites = attenuateEntites;
    function attenuateLines(entity, view, sizeInPixel) {
        const newSizeOfEntity = PaperUtils.getCoordinateOnView(view, sizeInPixel, 0).getDistance(PaperUtils.getCoordinateOnView(view, 0, 0));
        const entityCurrentSize = entity.strokeWidth;
        const newScale = newSizeOfEntity / entityCurrentSize;
        entity.strokeWidth *= newScale;
    }
    PaperUtils.attenuateLines = attenuateLines;
    function created2DMeasurement(lExtensionEnd, rExtensionEnd, precision = 2) {
        const yOffset = 5;
        const dist = Math.abs(lExtensionEnd.x - rExtensionEnd.x);
        const textPos = new Point((lExtensionEnd.x + rExtensionEnd.x) * 0.5, Math.max(lExtensionEnd.y, rExtensionEnd.y) + yOffset);
        const leftLine = createLine(lExtensionEnd, new Point(lExtensionEnd.x, lExtensionEnd.y + yOffset + 0.1 * yOffset), defaultMeasurementLineSettings.strokeColor);
        const rightLine = createLine(rExtensionEnd, new Point(rExtensionEnd.x, rExtensionEnd.y + yOffset + 0.1 * yOffset), defaultMeasurementLineSettings.strokeColor);
        const refLine = createLine(new Point(lExtensionEnd.x, lExtensionEnd.y + yOffset), new Point(rExtensionEnd.x, rExtensionEnd.y + yOffset), defaultMeasurementLineSettings.strokeColor);
        const text = createText(textPos, new Color(1, 1, 1), dist.toFixed(precision), 1);
        return { lines: [leftLine, rightLine, refLine], text };
    }
    PaperUtils.created2DMeasurement = created2DMeasurement;
    /**
     * Function calcualtes the projected length for the pixel size in a view
     * @param pixelSize
     * @param view
     */
    function pixelToProjectDist(pixelSize, view) {
        const p1 = view.viewToProject(new Point(0, 0));
        const p2 = view.viewToProject(new Point(pixelSize, 0));
        return p1.getDistance(p2);
    }
    PaperUtils.pixelToProjectDist = pixelToProjectDist;
    function projectToPixelDist(dist, view) {
        const p1 = view.projectToView(new Point(0, 0));
        const p2 = view.projectToView(new Point(dist, 0));
        return p1.getDistance(p2);
    }
    PaperUtils.projectToPixelDist = projectToPixelDist;
    /**
     * Function updates the position of the item based on specific offset from a reference position
     * Text will be repositioned towards a specific direction
     * Important: Item.data should have "moveDir" and "refPosition" properties for this function to work
     * @param item Item that needs to be updated
     * @param offsetFromRefPos pixel length that needs to be given
     * @param view paper view in which item is present
     */
    function setAttenuatedPos(item, offsetFromRefPos, view) {
        const translationDist = pixelToProjectDist(offsetFromRefPos, view);
        const pos = item.data.refPosition;
        const translation = item.data.moveDir
            .clone()
            .multiply(translationDist);
        item.position = translation.add(pos);
    }
    PaperUtils.setAttenuatedPos = setAttenuatedPos;
})(PaperUtils || (PaperUtils = {}));
//# sourceMappingURL=paper-utils.js.map